import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { map, Observable } from 'rxjs';
import { environment } from '../../environments/environment';

/** data payload of a successful POST /api/File/Upload. */
export interface UploadedFile {
  guid: string;
  key: string;
  fileName: string;
  contentType: string;
  url: string; // presigned, ~60 min
}

interface ApiEnvelope<T = any> {
  success: boolean;
  message: string;
  statusCode: number;
  data: T;
}

/** Client-side mirror of the server rules (server still enforces them). */
export const FILE_RULES = {
  maxBytes: 10 * 1024 * 1024,
  allowed: ['.jpg', '.jpeg', '.png', '.pdf', '.docx'],
};

/** Coerce the upload payload to {@link UploadedFile} regardless of camelCase/PascalCase. */
function normalizeUploaded(d: any): UploadedFile {
  d = d ?? {};
  return {
    guid: d.guid ?? d.Guid ?? '',
    key: d.key ?? d.Key ?? '',
    fileName: d.fileName ?? d.FileName ?? '',
    contentType: d.contentType ?? d.ContentType ?? '',
    url: d.url ?? d.Url ?? '',
  };
}

/**
 * Generic S3-backed file API. Features persist the returned {@link UploadedFile.guid}
 * (never a path); the private bucket is reached only via short-lived presigned URLs
 * minted on demand from that guid.
 */
@Injectable({ providedIn: 'root' })
export class FileService {
  private http = inject(HttpClient);
  private readonly baseUrl = `${environment.apiUrl}/File`;

  /** Upload one file under a module group (e.g. 'PartnerKyc'). Returns the stored {@link UploadedFile}. */
  upload(file: File, module: string): Observable<UploadedFile> {
    const form = new FormData();
    form.append('file', file);
    form.append('module', module);
    // Note: do NOT set Content-Type — the browser adds the multipart boundary,
    // and the app's interceptor only attaches Authorization (never Content-Type).
    return this.http.post<ApiEnvelope<any>>(`${this.baseUrl}/Upload`, form).pipe(
      map((res) => {
        if (!res?.success) throw new Error(res?.message || 'Upload failed');
        return normalizeUploaded(res.data);
      }),
    );
  }

  /** Fresh presigned URL for a previously saved file (to preview/open it). */
  url(guid: string): Observable<string> {
    return this.http
      .get<ApiEnvelope<any>>(`${this.baseUrl}/Url`, { params: { guid } })
      .pipe(map((res) => res?.data?.url ?? res?.data?.Url ?? ''));
  }

  /** Presigned URL + original file name (the latter drives the preview type + title). */
  info(guid: string): Observable<{ url: string; fileName: string }> {
    return this.http.get<ApiEnvelope<any>>(`${this.baseUrl}/Url`, { params: { guid } }).pipe(
      map((res) => ({
        url: res?.data?.url ?? res?.data?.Url ?? '',
        fileName: res?.data?.fileName ?? res?.data?.FileName ?? '',
      })),
    );
  }

  /** Authed binary download (interceptor adds the JWT) — used to force-save any file type. */
  download(guid: string): Observable<Blob> {
    return this.http.get(`${this.baseUrl}/Download`, { params: { guid }, responseType: 'blob' });
  }

  /** Remove an uploaded file (S3 object + record). */
  delete(guid: string) {
    return this.http.get<ApiEnvelope>(`${this.baseUrl}/Delete`, { params: { guid } });
  }

  /** Local pre-check so we fail fast before hitting the network. */
  validate(file: File): string | null {
    const dot = file.name.lastIndexOf('.');
    const ext = dot >= 0 ? file.name.slice(dot).toLowerCase() : '';
    if (!FILE_RULES.allowed.includes(ext))
      return `File type ${ext || '(none)'} not allowed. Allowed: ${FILE_RULES.allowed.join(', ')}`;
    if (file.size > FILE_RULES.maxBytes) return 'File exceeds the 10 MB limit.';
    return null;
  }
}
